import { createContext, useState, useEffect } from "react";
import { Alert, AppState } from "react-native";
import { supabase } from "@/lib/supabase-client";
import { Session, User } from "@supabase/supabase-js";
import { jwtDecode } from "jwt-decode";
import { devSleep } from "@/lib/helpers";
import { useStorage } from "@/hooks/useStorage";
import { DisplayNamesSchema } from "@/types/db/tenants";
import { UserStatusType } from "@/types/db/users";
import { router } from "expo-router";

import type { UserRoleType, JwtClaims } from "@/types/auth";
import type { AuthContextType } from "@/types/providers/auth";

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const storage = useStorage();

  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [lastUsedEmail, setLastUsedEmail] = useState(storage.getItem("email") ?? "");
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRoleType[] | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [userStatus, setUserStatus] = useState<UserStatusType | null>(null);
  const [passwordLastUpdated, setPasswordLastUpdated] = useState<string | null>(null);
  const [needsPasswordUpdate, setNeedsPasswordUpdate] = useState<boolean>(false);
  const [isRecoverySession, setIsRecoverySession] = useState<boolean>(false);
  const [displayNames, setDisplayName] = useState(DisplayNamesSchema.parse(undefined));

  useEffect(() => {
    setLoading(true);
    const init = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session ?? null);
      setUser(data.session?.user ?? null);
    };
    init();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      setLoading(true);
      setSession(session);
      setUser(session?.user ?? null);
      if (event === "PASSWORD_RECOVERY") {
        setIsRecoverySession(true);
      }
      if (session?.access_token) {
        try {
          const jwt = jwtDecode<JwtClaims>(session.access_token);
          setUserRoles(jwt.roles ?? null);
          setTenantId(jwt.tenant_id ?? null);
          setUserStatus((jwt.user_status ?? null) as UserStatusType | null);
          setPasswordLastUpdated(jwt.password_last_updated ?? null);
          setNeedsPasswordUpdate(jwt.needs_password_update ?? false);
        } catch (err) {
          setUserRoles(null);
          setTenantId(null);
          setUserStatus(null);
          setPasswordLastUpdated(null);
          setNeedsPasswordUpdate(false);
        }
      } else {
        setUserRoles(null);
        setTenantId(null);
        setUserStatus(null);
        setPasswordLastUpdated(null);
        setNeedsPasswordUpdate(false);
      }
      setLoading(false);
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleAppStateChange = async (state: string) => {
      if (state === "active") {
        supabase.auth.startAutoRefresh();

        // Only fetch if we have a tenantId
        if (tenantId) {
          try {
            const { data, error } = await supabase.from("tenants").select("meta_data").eq("id", tenantId).single();

            if (!error && data?.meta_data?.info?.display_names) {
              setDisplayName(DisplayNamesSchema.parse(data.meta_data.info.display_names));
            }
          } catch (err) {
            console.error("Failed to fetch display names:", err);
            // Keep the default parsed values
          }
        }
      } else {
        supabase.auth.stopAutoRefresh();
      }
    };

    // Add the listener
    const subscription = AppState.addEventListener("change", handleAppStateChange);

    // Also fetch on mount if app is active
    if (AppState.currentState === "active" && tenantId) {
      handleAppStateChange("active");
    }

    return () => {
      subscription?.remove();
    };
  }, [tenantId]); // Add tenantId as dependency

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    await devSleep();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) throw new Error(error.message);
    setLastUsedEmail(email);
    storage.saveFast("email", email);
  };

  const updatePassword = async (currentPassword: string, newPassword: string, isRecovery?: boolean) => {
    if (!session?.access_token) {
      throw new Error("Not authenticated");
    }

    const { data, error } = await supabase.functions.invoke("update-password", {
      body: { currentPassword: isRecovery ? undefined : currentPassword, newPassword, isFirstTimeSetup: isRecovery },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (error) {
      throw new Error(error.message || "Failed to update password");
    }

    if (!data?.success) {
      throw new Error(data?.message || "Failed to update password");
    }

    // Clear recovery flag and refresh session to get updated JWT claims
    setIsRecoverySession(false);
    await supabase.auth.refreshSession();
  };

  const signOut = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setUser(null);
    setUserRoles(null);
    setTenantId(null);
    setUserStatus(null);
    setPasswordLastUpdated(null);
    setNeedsPasswordUpdate(false);
    setIsRecoverySession(false);
    setLoading(false);
    router.replace("/sign-in");
  };

  const signUp = async (email: string, password: string) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) Alert.alert("Sign Up Error", error.message);
    if (!data.session) Alert.alert("Please check your inbox for email verification!");
    setLoading(false);
  };
  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        tenantId,
        userRoles,
        userStatus,
        passwordLastUpdated,
        needsPasswordUpdate,
        isRecoverySession,
        signIn,
        signUp,
        signOut,
        updatePassword,
        lastUsedEmail,
        displayNames,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
